/**
 * Storefront search history — persisted in localStorage.
 *
 * Stores the last few queries the customer typed so we can show them in the
 * search dropdown when they refocus the empty input. All access is wrapped
 * in try/catch because localStorage can throw in strict-mode iframes,
 * Safari private browsing, and any other quota / serialization edge.
 *
 * Keep this module client-side-pure (no Remix imports) so it can be called
 * directly from `useEffect` and event handlers without SSR concerns.
 */

const STORAGE_KEY = "eql_search_history";
const MAX_ENTRIES = 5;
const MIN_QUERY_LENGTH = 2;

export function loadSearchHistory(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((s): s is string => typeof s === "string" && s.trim().length >= MIN_QUERY_LENGTH)
      .slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

export function saveSearchHistory(query: string): string[] {
  if (typeof window === "undefined") return [];
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return loadSearchHistory();
  try {
    const current = loadSearchHistory();
    // De-duplicate case-insensitively, keep the new entry first (LRU semantics).
    const next = [
      trimmed,
      ...current.filter((s) => s.toLowerCase() !== trimmed.toLowerCase()),
    ].slice(0, MAX_ENTRIES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadSearchHistory();
  }
}

export function clearSearchHistory(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function removeSearchHistoryEntry(query: string): string[] {
  if (typeof window === "undefined") return [];
  const target = query.trim().toLowerCase();
  if (!target) return loadSearchHistory();
  try {
    const current = loadSearchHistory();
    const next = current.filter((s) => s.toLowerCase() !== target);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadSearchHistory();
  }
}
